import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AssessmentDelete from "./AssessmentDelete";
import {
  Pencil,
  Trash2,
  GraduationCap,
  FileClock,
  Trophy,
  FileText,
  BarChart3,
  Award,
} from "lucide-react";

function OfflineDraftAssessBoxList({
  assessments = [],
  getDraftAssessment,
  isLoading,
}) {
  const navigate = useNavigate();
  const [delete1, setDelete1] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteName, setDeleteName] = useState("");

  const getMarksDisplay = (a) => {
    if (a?.total_marks) return `${a.total_marks} Marks`;
    if (a?.entryType === "Grade" || a?.gradeScaleId) return "Grade Based";
    return "—";
  };

  const getEntryTypeBadge = (a) => {
    const isMarks = a?.entryType === "Marks" || a?.total_marks;
    return {
      text: isMarks ? "Marks" : "Grade",
      icon: isMarks ? <BarChart3 size={12} /> : <Award size={12} />,
      bg: isMarks ? "#dcfce7" : "#fef9c3",
      color: isMarks ? "#166534" : "#854d0e",
      border: isMarks ? "#bbf7d0" : "#fde047",
    };
  };

  const getSubjectColor = (color) => {
    return color ? `#${color}` : "#8D4B28";
  };

  const handleEdit = (assessment) => {
    navigate("/offline-submission-report", {
      state: { assessmentId: assessment.id, assessment: assessment },
    });
  };

  const handleDeleteClick = (id, name, e) => {
    e.stopPropagation();
    setDeleteId(id);
    setDeleteName(name);
    setDelete1(true);
  };

  if (isLoading) {
    return (
      <div className="w-100 d-flex justify-content-center py-5">
        <div
          className="spinner-border"
          style={{ color: "#f77f00" }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center py-5"
        style={{ minHeight: "300px", color: "#9ca3af" }}
      >
        <GraduationCap
          size={48}
          className="mb-3"
          style={{ opacity: 0.4, color: "#d1d5db" }}
        />
        <p className="fw-semibold fs-5" style={{ color: "#4b5563" }}>
          🧾 No Draft Assessments Found
        </p>
        <p className="small">Try adjusting filters or add a new assessment.</p>
      </div>
    );
  }

  return (
    <div
      className="w-100"
      style={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      <div
        className="table-responsive"
        style={{
          maxHeight: "calc(100vh - 200px)", // 👈 fixed height chahiye sticky ke liye
          overflow: "auto",
        }}
      >
        <table className="table align-middle mb-0" style={{ fontSize: "13px" }}>
          <thead>
            <tr
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "#f9fafb", // 👈 background must be solid
                // (already hai bas position/zIndex add karo)
              }}
            >
              <th
                className="text-center fw-bold py-3 px-3"
                style={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                Class
              </th>
              <th
                className="text-center fw-bold py-3 px-3"
                style={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                Subject
              </th>
              <th
                className="text-center fw-bold py-3 px-3"
                style={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                Event
              </th>
              <th
                className="text-center fw-bold py-3 px-3"
                style={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                Type
              </th>
              <th
                className="text-center fw-bold py-3 px-3"
                style={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                Mode
              </th>
              <th
                className="text-center fw-bold py-3 px-3"
                style={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                Marks
              </th>
              <th
                className="text-center fw-bold py-3 px-3"
                style={{
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {assessments.map((assessment, idx) => {
              const subjectColor = getSubjectColor(
                assessment?.subject_id?.subject_color,
              );
              const eventName = assessment?.assessmentEventId?.name || "N/A";
              const typeName =
                assessment?.assessmentTypeId?.name ||
                assessment?.type_id?.name ||
                "N/A";
              const entryBadge = getEntryTypeBadge(assessment);
              const isLast = idx === assessments.length - 1;

              return (
                <tr
                  key={assessment.id}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    borderBottom: isLast ? "none" : "1px solid #f3f4f6",
                  }}
                  onClick={() => handleEdit(assessment)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fffbeb";
                    e.currentTarget.style.transform = "scale(1.002)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <td
                    className="text-center py-3 px-3"
                    style={{ fontWeight: 600, color: "#1f2937" }}
                  >
                    <div className="d-flex align-items-center justify-content-center gap-1">
                      <span
                        style={{
                          backgroundColor: "#f3f4f6",
                          color: "#4b5563",
                          padding: "4px 10px",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: 700,
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        {assessment.standard_id?.name}
                      </span>
                      <span style={{ color: "#d1d5db" }}>-</span>
                      <span
                        style={{
                          backgroundColor: "#fef3c7",
                          color: "#92400e",
                          padding: "4px 10px",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: 700,
                          border: "1px solid #fde68a",
                        }}
                      >
                        {assessment.section_id?.name}
                      </span>
                    </div>
                  </td>

                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: subjectColor + "15",
                        color: subjectColor,
                        fontSize: "12px",
                        fontWeight: 700,
                        padding: "6px 14px",
                        borderRadius: "20px",
                        border: `1.5px solid ${subjectColor}40`,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: subjectColor,
                          display: "inline-block",
                        }}
                      />
                      {assessment.subject_id?.name}
                    </span>
                  </td>

                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#fdf4ff",
                        color: "#7c3aed",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "5px 12px",
                        borderRadius: "8px",
                        border: "1.5px solid #e9d5ff",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <Trophy size={12} />
                      {eventName}
                    </span>
                  </td>

                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: "#f0f9ff",
                        color: "#0369a1",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "5px 12px",
                        borderRadius: "8px",
                        border: "1.5px solid #bae6fd",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <FileText size={12} />
                      {typeName}
                    </span>
                  </td>

                  <td className="text-center py-3 px-3">
                    <span
                      style={{
                        backgroundColor: entryBadge.bg,
                        color: entryBadge.color,
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "5px 12px",
                        borderRadius: "20px",
                        border: `1.5px solid ${entryBadge.border}`,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {entryBadge.icon}
                      {entryBadge.text}
                    </span>
                  </td>

                  <td
                    className="text-center py-3 px-3"
                    style={{
                      fontWeight: 700,
                      color: "#f77f00",
                      fontSize: "13px",
                    }}
                  >
                    {getMarksDisplay(assessment)}
                  </td>

                  <td className="text-center py-3 px-3">
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        className="btn d-inline-flex align-items-center gap-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(assessment);
                        }}
                        style={{
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#fff",
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "10px",
                          boxShadow: "0 4px 12px rgba(59,130,246,0.25)",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 6px 20px rgba(59,130,246,0.35)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow =
                            "0 4px 12px rgba(59,130,246,0.25)";
                        }}
                      >
                        <Pencil size={14} />
                        Edit
                      </button>

                      <button
                        className="btn d-inline-flex align-items-center gap-1"
                        onClick={(e) =>
                          handleDeleteClick(assessment.id, assessment.name, e)
                        }
                        style={{
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#fff",
                          background:
                            "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "10px",
                          boxShadow: "0 4px 12px rgba(239,68,68,0.25)",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 6px 20px rgba(239,68,68,0.35)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow =
                            "0 4px 12px rgba(239,68,68,0.25)";
                        }}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {delete1 && (
        <AssessmentDelete
          assessmentId={deleteId}
          setDelete={setDelete1}
          getDraftAssessment={getDraftAssessment}
        />
      )}
    </div>
  );
}

export default OfflineDraftAssessBoxList;
